import { faEdit, faTimes } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { updateAdmin } from "../../redux/features/admin/adminSlice";
import { toast } from "react-toastify";
import { fetchRoles } from "../../redux/features/roles/rolesSlice";
import { fetchBrands } from "../../redux/features/brands/brandsSlice";

function UpdateAdmin({ adminId, onChange }) {
  const dispatch = useDispatch();
  const { list, updateAdminLoading } = useSelector((state) => state.admin);
  const { roles } = useSelector((state) => state.roles);
  const { brands } = useSelector((state) => state.brands);
  const [originalData, setOriginalData] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [formData, setFormData] = useState({
    id: "",
    name: "",
    email: "",
    role_id: "",
    brand_id: "",
  });
  const [errors, setErrors] = useState({});

  const handleOpen = () => setIsModalOpen(true);
  const handleClose = () => setIsModalOpen(false);

  useEffect(() => {
    dispatch(fetchRoles());
    dispatch(fetchBrands());
  }, [dispatch]);

  // ✅ Prefill form with existing admin data
  useEffect(() => {
    const adminToEdit = list.find((a) => a.id === adminId);
    if (adminToEdit) {
      const { id, name, email, roles, brand } = adminToEdit;
      setFormData({
        id,
        name,
        email,
        role_id: roles?.[0]?.id || "",
        brand_id: brand?.id,
      });
      setOriginalData({
        id,
        name,
        email,
        role_id: roles?.[0]?.id || "",
        brand_id: brand?.id,
      });
    }
  }, [adminId, list]);

  // ✅ Handle input change
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const hasChanges = () => {
    if (!originalData) return true;
    return (
      formData.name !== originalData.name ||
      formData.email !== originalData.email ||
      formData.role_id !== originalData.role_id ||
      formData.brand_id !== originalData.brand_id
    );
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.name.trim()) newErrors.name = "Name is required.";
    if (!formData.role_id) newErrors.role_id = "Role is required.";
    // if (!formData.brand_id) newErrors.brand_id = "Brand is required.";
    if (!formData.email.trim()) newErrors.email = "Email is required.";
    else if (!/\S+@\S+\.\S+/.test(formData.email))
      newErrors.email = "Invalid email format.";

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0; // returns true if no errors
  };

  // ✅ Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) {
      toast.error("Please fill all required fields correctly.");
      return;
    }

    if (!hasChanges()) {
      toast.info("No changes detected");
      return;
    }
    try {
      await dispatch(updateAdmin(formData)).unwrap();
      toast.success("Admin Updated successfully!");
      setIsModalOpen(false); // close modal on success
      onChange();
    } catch (error) {
      if (error?.errors) {
        const formattedErrors = {};

        Object.keys(error.errors).forEach((key) => {
          formattedErrors[key] = error.errors[key][0];
        });

        setErrors(formattedErrors);
      } else if (error?.message) {
        toast.error(error.message);
      } else {
        toast.error("Updates Failed");
      }
    }
  };

  return (
    <div>
      <button
        onClick={handleOpen}
        className="text-green-600 hover:text-green-700 font-medium"
      >
        <FontAwesomeIcon icon={faEdit} />
      </button>

      {/* Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50">
          <div className="bg-white rounded-2xl shadow-lg w-[45%] p-10 relative">
            <button
              onClick={handleClose}
              className="absolute top-4 right-4 text-gray-500 hover:text-gray-700"
            >
              <FontAwesomeIcon icon={faTimes} />
            </button>

            <h2 className="text-xl font-semibold border-b border-gray-200 mb-6 pb-5 text-left">
              Update Admin
            </h2>

            <form className="space-y-10" onSubmit={handleSubmit}>
              <div className="grid grid-cols-2 gap-5">
                <div>
                  <label
                    htmlFor="name"
                    className="block text-gray-700 font-medium mb-1 text-left"
                  >
                    Name
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    className="w-full border border-gray-300 rounded-md p-2 focus:outline-none focus:ring-2 focus:ring-purpleColor"
                  />
                  {errors.name && (
                    <p className="text-red-500 text-sm mt-1 text-left">
                      {errors.name}
                    </p>
                  )}
                </div>

                <div>
                  <label
                    htmlFor="email"
                    className="block text-gray-700 font-medium mb-1 text-left"
                  >
                    Email
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    className="w-full border border-gray-300 rounded-md p-2 focus:outline-none focus:ring-2 focus:ring-purpleColor"
                  />
                  {errors.email && (
                    <p className="text-red-500 text-sm mt-1 text-left">
                      {errors.email}
                    </p>
                  )}
                </div>
                <div>
                  <label className="block text-gray-700 font-medium mb-1 text-left">
                    Role
                  </label>
                  <select
                    name="role_id"
                    value={formData.role_id}
                    onChange={handleChange}
                    className="w-full border border-gray-300 rounded-md p-2 focus:outline-none focus:ring-2 focus:ring-purpleColor"
                  >
                    <option value="" hidden>
                      Select Role
                    </option>
                    {roles?.map((role) => (
                      <option key={role.id} value={role.id}>
                        {role.name}
                      </option>
                    ))}
                  </select>
                  {errors.role_id && (
                    <p className="text-red-500 text-sm mt-1 text-left">
                      {errors.role_id}
                    </p>
                  )}
                </div>
                <div>
                  <label className="block text-gray-700 font-medium mb-1 text-left">
                    brand
                  </label>
                  <select
                    name="brand_id"
                    value={formData.brand_id}
                    onChange={handleChange}
                    className="w-full border border-gray-300 rounded-md p-2 focus:outline-none focus:ring-2 focus:ring-purpleColor"
                  >
                    <option value="" hidden>
                      Select Brand
                    </option>
                    {brands?.map((brand) => (
                      <option key={brand.id} value={brand.id}>
                        {brand.name}
                      </option>
                    ))}
                  </select>
                  {errors.brand_id && (
                    <p className="text-red-500 text-sm mt-1 text-left">
                      {errors.brand_id}
                    </p>
                  )}
                </div>
              </div>

              <div className="flex justify-end gap-3 mt-6">
                <button
                  type="button"
                  onClick={handleClose}
                  className="px-4 py-2 rounded-md border border-gray-300 hover:bg-gray-100"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={updateAdminLoading}
                  className={`px-4 py-2 rounded-md text-white ${
                    updateAdminLoading
                      ? "bg-lightPurple/60 cursor-not-allowed"
                      : "bg-lightPurple hover:bg-purpleColor"
                  }`}
                >
                  {updateAdminLoading ? "Saving..." : "Save"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default UpdateAdmin;
